import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { EventStatus, EventType } from "@/types/enums/event";
import type { EventDetail, UpdatedAppointment } from "@/types/event";
import type { PatientChart } from "@/types/patient";
import { updatePatientInCache } from "@/api/provider/user/patient/patient-cache-utils";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";

/**
 * Subscribe to appointment-related websocket events and keep the patient's
 * appointment list in sync in React Query cache.
 */
export function usePatientRealtimeCache() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleMessage(message) {
      const patientId = message.patientId;
      if (!patientId) {
        return;
      }
      const key = eventQueryKeys.searchPatientEvents(patientId, "");
      bb5: switch (message.type) {
        case AlertType.APPOINTMENT_SCHEDULED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT:
          {
            const newEvent = message.data as EventDetail;
            if (!newEvent.attendees?.some(a_1 => a_1.user.id === patientId) || newEvent.type !== EventType.APPOINTMENT) {
              return;
            }
            const hadCache_0 = !!queryClient.getQueryData(key);
            queryClient.setQueryData(key, prev_3 => {
              if (!prev_3) {
                return [newEvent];
              }
              if (prev_3.some(e_1 => e_1.id === newEvent.id)) {
                return prev_3;
              }
              return [...prev_3, newEvent];
            });
            if (!hadCache_0) {
              queryClient.invalidateQueries({
                queryKey: key
              });
            }
            break bb5;
          }
        case AlertType.APPOINTMENT_UPDATED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT_UPDATED:
          {
            const updatedEvent = message.type === AlertType.APPOINTMENT_UPDATED ? (message.data as UpdatedAppointment).updatedTo : message.data as EventDetail;
            if (!updatedEvent.attendees?.some(a_0 => a_0.user.id === patientId) || updatedEvent.type !== EventType.APPOINTMENT) {
              return;
            }
            const hadCache = !!queryClient.getQueryData(key);
            queryClient.setQueryData(key, prev_1 => {
              if (!prev_1) {
                return [updatedEvent];
              }
              return prev_1.map(e_0 => e_0.id === updatedEvent.id ? updatedEvent : e_0);
            });
            if (!hadCache) {
              queryClient.invalidateQueries({
                queryKey: key
              });
            }
            const detailKey_0 = eventQueryKeys.detail(updatedEvent.id);
            queryClient.setQueryData(detailKey_0, prev_2 => prev_2 ? {
              ...prev_2,
              ...updatedEvent
            } : updatedEvent);
            break bb5;
          }
        case AlertType.APPOINTMENT_CANCELLED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT_CANCELLED:
          {
            const cancelledEvent = message.data as EventDetail;
            if (!cancelledEvent.attendees?.some(a => a.user.id === patientId)) {
              return;
            }
            queryClient.setQueryData(key, prev => {
              if (!prev) {
                return prev;
              }
              return prev.map(e => e.id === cancelledEvent.id ? {
                ...e,
                status: EventStatus.CANCELLED
              } : e);
            });
            const detailKey = eventQueryKeys.detail(cancelledEvent.id);
            queryClient.setQueryData(detailKey, _temp);
            break bb5;
          }
        case AlertType.PATIENT_UPDATED:
          {
            const payload = message.data as Partial<PatientChart> & {
              id?: string;
            };
            if (!payload?.id) {
              break bb5;
            }
            updatePatientInCache(payload.id, payload);
            break bb5;
          }
        default:
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleMessage = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [AlertType.APPOINTMENT_SCHEDULED, AlertType.APPOINTMENT_UPDATED, AlertType.APPOINTMENT_CANCELLED, AlertType.SELF_SCHEDULED_APPOINTMENT, AlertType.SELF_SCHEDULED_APPOINTMENT_UPDATED, AlertType.SELF_SCHEDULED_APPOINTMENT_CANCELLED, AlertType.PATIENT_UPDATED];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useWebSocketCallbackSubscription(t1, handleMessage, true, "usePatientRealtimeCache");
}
function _temp(prev_0) {
  return prev_0 ? {
    ...prev_0,
    status: EventStatus.CANCELLED
  } : prev_0;
}